<template>
    <common-content-spacer height="medium">
        <common-content-spacer height="small">
            <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>
            <common-page-header :heading="pageTitle"></common-page-header>
        </common-content-spacer>
        <common-loader :isProcessing="isSending"></common-loader>
        <common-page-plain v-if="referrals.length && !isSending">
            <common-content-spacer height="medium">
                <referrals-index-listing
                    :referrals="referrals"
                    :isFromPatient="isFromPatient"
                    :type="$route.params.type"
                    :filterOptions="filterOptions"
                ></referrals-index-listing>
                <common-buttons>
                    <common-button
                        v-if="referrals.length < referralsTotalCount"
                        :isProcessing="isProcessing"
                        :label="$t('pages.referrals.index.buttons.show_more')"
                        type="primary"
                        size="large"
                        @click="showMore()"
                    ></common-button>
                </common-buttons>
            </common-content-spacer>
        </common-page-plain>
        <div v-if="!referrals.length && !isSending">{{ $t('pages.referrals.index.empty_state.no_referrals') }}</div>
    </common-content-spacer>
</template>

<script>
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralsIndexListing from 'components/page-elements/referrals/ReferralsIndexListing'
    import DemoReferral from 'services/DemoReferral'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin],

        components: {
            ReferralsIndexListing,
        },

        props: {
            isFromPatient: { type: Boolean, required: false, default: false },
        },

        data() {
            return {
                referrals: [],
                offset: 0,
                referralsTotalCount: 0,
                isSending: false,
                isProcessing: false,
                allFilterOptions: [
                    {
                        label: this.$t('pages.referrals.index.filters.pending_confirmation'),
                        status: 'CREATED',
                        value: true,
                        state: 'open',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.patient_visit_pending'),
                        status: 'PATIENT_VISIT_PENDING',
                        value: true,
                        state: 'open',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.pending_insurance_auth'),
                        status: 'INSURANCE_AUTH_PENDING',
                        value: true,
                        state: 'open',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.pending_consultation_report'),
                        status: 'CONSULTATION_NOTES_PENDING',
                        value: true,
                        state: 'open',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.to_be_rescheduled'),
                        status: 'RESCHEDULE_REQUESTED',
                        value: true,
                        state: 'open',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.out_of_availability'),
                        status: 'RESCHEDULE_AVAILABILITY_CHANGE',
                        value: true,
                        state: 'open',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.declined'),
                        status: 'DECLINED',
                        value: true,
                        state: 'closed',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.cancelled'),
                        status: 'CANCELLED',
                        value: true,
                        state: 'closed',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.no_show'),
                        status: 'NO_SHOW',
                        value: true,
                        state: 'closed',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.completed'),
                        status: 'COMPLETED',
                        value: true,
                        state: 'closed',
                    },
                ],
                filterOptions: [],
            }
        },

        computed: {
            pageTitle() {
                return this.$t(`pages.referrals.index.titles.${this.$route.params.type}`)
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.referrals.index.breadcrumbs.referrals'),
                        route: { name: `referrals.${this.$route.params.type}.referrals` },
                    },
                    { label: this.$t(`pages.referrals.index.breadcrumbs.${this.$route.params.type}`) },
                ]
            },
        },

        methods: {
            loadReferrals(params, append) {
                if (!append) {
                    this.isSending = true
                } else {
                    this.isProcessing = true
                }

                ReferralsAPI.getList(
                    this.$route.params.type,
                    this.$auth.getActiveMember() && this.$auth.getActiveMember()?.id
                        ? this.$auth.getActiveMember()?.id
                        : this.$auth.getUser().id,
                    params,
                )
                    .then((response) => {
                        if (append) {
                            this.referrals = this.referrals.concat(response)
                            return
                        }
                        this.referralsTotalCount = response._headers['x-total-count']
                        this.referrals = response
                    })
                    .finally(() => {
                        this.isSending = false
                        this.isProcessing = false
                    })
            },

            changeTab(event) {
                this.$router.push({ name: `referrals.${this.$route.params.type}.${event}` })
                this.activeTab = event
            },

            showMore() {
                this.offset = this.offset + Number(this.$route.query.limit)
                this.loadReferrals({ ...this.$route.query, offset: this.offset }, true)
            },

            setFilterOptions() {
                const state = this.$route.query.state
                this.filterOptions =
                    state && state !== 'all'
                        ? this.getResetFilterOptions().filter((option) => option.state === state)
                        : this.getResetFilterOptions()
            },

            getResetFilterOptions() {
                return this.allFilterOptions.map((option) => {
                    option.value = true
                    return option
                })
            },
        },

        watch: {
            referrals() {
                if (this.referrals._headers) {
                    this.referralsTotalCount = this.referrals._headers['x-total-count']
                }
            },

            '$route.params.tabType'() {
                this.referrals = []
                if (!this.isFromPatient) {
                    this.loadReferrals({ ...this.$route.query, offset: this.offset })
                } else {
                    this.loadReferrals({ ...this.$route.query, offset: this.offset, isFromPatient: true })
                }
            },

            '$route.params.type'() {
                this.referrals = []
                if (!this.isFromPatient) {
                    this.loadReferrals({ ...this.$route.query, offset: this.offset })
                } else {
                    this.loadReferrals({ ...this.$route.query, offset: this.offset, isFromPatient: true })
                }
            },

            '$route.query.state'() {
                this.setFilterOptions()
            },
        },

        mounted() {
            if (this.$auth.isUserPracticeDemo()) {
                if (this.$route.params.type === 'made') {
                    this.referrals = []
                } else {
                    this.referrals = DemoReferral.getDemoReferral()
                }
            } else {
                this.offset = 0

                if (!this.isFromPatient) {
                    this.loadReferrals({ ...this.$route.query, offset: this.offset })
                } else {
                    this.loadReferrals({ ...this.$route.query, offset: this.offset, isFromPatient: true })
                }
            }
            this.setFilterOptions()
        },
    }
</script>
